import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, message } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { ClipLoader } from 'react-spinners';
import { useSelector } from 'react-redux';

const CreateModule = ({ visible, handleClose, initialData = '', onSubmit, courses = [] }) => {
  const loading = useSelector((state) => state.Module.loading);
  const [previewImage, setPreviewImage] = useState('');

  // existing thumbnail when editing
  useEffect(() => {
    if (initialData?.thumbnail) {
      setPreviewImage(initialData.thumbnail);
    }
  }, [initialData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseId: initialData?.courseId?._id || '',
      title: initialData?.title || '',
      orderIndex: initialData?.orderIndex || '',
      thumbnail: initialData?.thumbnail || '',
    },
    validationSchema: Yup.object({
      courseId: Yup.string().required('Course is required'),
      title: Yup.string().required('Title is required'),
      orderIndex: Yup.number()
        .typeError('Order must be a number')
        .required('Order is required')
        .min(0, 'Order must be a non-negative number'),
      thumbnail: Yup.string().required('Thumbnail is required'),
    }),
    onSubmit: (values, { resetForm }) => {
      const payload = {
        courseId: values.courseId,
        title: values.title,
        orderIndex: values.orderIndex,
        thumbnail: values.thumbnail,
      };

      if (!initialData) {
        onSubmit(payload, onClose);
      } else {
        onSubmit(initialData?._id, payload, onClose);
      }
    },
  });

  const onClose = () => {
    formik.resetForm();
    setPreviewImage('');
    handleClose();
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.error('Please upload a valid image');
      return;
    }
    const base64 = await getBase64(file);
    setPreviewImage(base64);
    formik.setFieldValue('thumbnail', base64);
  };

  return (
    <Modal
      title={initialData ? 'Edit Module' : 'Create New Module'}
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={500}
      className="custom-modal-header p-0"
      maskClosable={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">

          {/* Course */}
          <div className="col-12">
            <label className="modal-form">
              Course <span className="text-danger">*</span>
            </label>
            <Select
              placeholder="Select course"
              options={courses.map((c) => ({ label: c.title, value: c._id }))}
              className="w-100"
              value={
                courses
                  .map((c) => ({ label: c.title, value: c._id }))
                  .find((opt) => opt.value === formik.values.courseId) || null
              }
              onChange={(option) => formik.setFieldValue('courseId', option?.value || '')}
              isClearable
            />
            {formik.touched.courseId && formik.errors.courseId && (
              <span style={{ color: 'red' }}>{formik.errors.courseId}</span>
            )}
          </div>

          {/* Title */}
          <div className="col-12">
            <label className="modal-form">
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control fs-7"
              placeholder="Enter title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />
            {formik.touched.title && formik.errors.title && (
              <span style={{ color: 'red' }}>{formik.errors.title}</span>
            )}
          </div>

          {/* Order Index */}
          <div className="col-12">
            <label className="modal-form">
              Order <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className="form-control fs-7"
              placeholder="Enter order index"
              name="orderIndex"
              value={formik.values.orderIndex}
              onChange={formik.handleChange}
              min="1"
            />
            {formik.touched.orderIndex && formik.errors.orderIndex && (
              <span style={{ color: 'red' }}>{formik.errors.orderIndex}</span>
            )}
          </div>

          {/* Thumbnail */}
          <div className="col-12">
            <label className="modal-form">
              Thumbnail <span className="text-danger">*</span>
            </label>
            <div className="form-control fs-7 p-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="form-control border-0 p-0"
                style={{ boxShadow: 'none' }}
              />
            </div>
            {formik.touched.thumbnail && formik.errors.thumbnail && (
              <span style={{ color: 'red' }}>{formik.errors.thumbnail}</span>
            )}

            {/* Preview */}
            {previewImage && (
              <div
                className="mt-2 text-center"
                style={{
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <img
                  src={previewImage}
                  alt="Thumbnail Preview"
                  style={{
                    width: '120px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer mt-3">
            <button type="button" className="btn btn-light btn-md" onClick={onClose}>
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md ms-3"
              style={{ minWidth: '80px' }}
              disabled={loading}
            >
              {loading ? (
                <ClipLoader size={16} color="white" />
              ) : initialData ? (
                'Update'
              ) : (
                'Add'
              )}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

CreateModule.propTypes = {
  visible: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  courses: PropTypes.array.isRequired,
};

export default CreateModule;
